import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Breadcrumbs, PageHeader, Table, type TableColumn } from '~/components/data';
import { Button } from '~/components/primitives';
import { DocStatusBadge, type DocStatusValue } from '~/components/status';
import { fetchStockReconciliation } from '~/lib/api/stock-reconciliation';
import { formatDate, formatQty } from '~/lib/format';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import shared from './_auth._shared.module.css';

/**
 * EFE-9 — Stock Reconciliation detail page. Mirrors SE detail pattern.
 */
export const Route = createFileRoute('/_auth/stock-reconciliation/$id/')({
  component: SrDetail,
});

/** FE-58/59 (QA-2026-05-27): BE returns `item_label: "{sku} — {name}"` + `physical_qty` (not item_name/item_sku/counted_qty). Adapt locally. */
type SrLine = {
  item_id: string;
  item_label?: string | null;
  warehouse_id?: string | null;
  batch_no?: string | null;
  expected_qty?: string | null;
  physical_qty?: string | null;
  variance_qty?: string | null;
  uom?: string;
};

function parseItemLabel(label: string | null | undefined): { sku: string; name: string } {
  if (!label) return { sku: '—', name: '—' };
  const parts = label.split(' — ');
  if (parts.length >= 2) return { sku: parts[0] ?? '—', name: parts.slice(1).join(' — ') };
  return { sku: '—', name: label };
}

function SrDetail(): JSX.Element {
  const { id } = Route.useParams();
  const scope = useUserScope();
  // BE-13 (tails-sweep-2): `stock_reconciliation` заведена в каноне ABILITIES —
  // инвентаризация больше не заимствует право у складского документа.
  const canEdit = canDo(scope, 'stock_reconciliation', 'update');

  const query = useQuery({
    queryKey: ['stock-reconciliation', 'detail', id],
    queryFn: () => fetchStockReconciliation(id),
    staleTime: 10_000,
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;

  const sr = query.data as unknown as {
    id: string;
    voucher_no: string | null;
    kind: 'opening_stock' | 'standard';
    warehouse_id: string | null;
    warehouse_name: string | null;
    status: 'draft' | 'submitted' | 'cancelled';
    posting_datetime?: string;
    created_at: string;
    updated_at: string;
    notes?: string | null;
    lines: SrLine[];
  };

  const isDraft = sr.status === 'draft';

  const lineColumns: TableColumn<SrLine>[] = [
    {
      id: 'item',
      header: m.route_sr_col_item(),
      // FE-59 (QA-2026-05-27): never render raw UUID — fallback на parsed item_label
      accessor: (r) => parseItemLabel(r.item_label).name,
    },
    {
      id: 'sku',
      header: 'SKU',
      accessor: (r) => parseItemLabel(r.item_label).sku,
      mono: true,
    },
    {
      id: 'batch',
      header: m.route_sr_col_batch(),
      accessor: (r) => r.batch_no ?? '—',
    },
    {
      id: 'qty',
      header: m.route_sr_col_counted_qty(),
      // FE-58 follow-up: formatQty (precision per UoM + null-guard + tabular trim).
      accessor: (r) => formatQty(r.physical_qty, r.uom),
      mono: true,
      align: 'right',
    },
  ];

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_stock_reconciliation(), to: '/stock-reconciliation' },
              { label: sr.voucher_no ?? id.slice(0, 8) },
            ]}
          />
        }
        title={`${m.nav_stock_reconciliation()} · ${sr.voucher_no ?? id.slice(0, 8)}`}
        actions={
          <div className={shared.actions}>
            {isDraft && canEdit && (
              <Link to="/stock-reconciliation/$id/edit" params={{ id }}>
                <Button variant="secondary">{m.common_button_edit()}</Button>
              </Link>
            )}
          </div>
        }
      />
      <div className={shared.statGrid4Lg}>
        <div>
          <div className={shared.statLabel}>{m.route_sr_col_warehouse()}</div>
          <div>{sr.warehouse_name ?? '—'}</div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.route_sr_col_kind()}</div>
          {/* WHS-F41: тип — тем же словарём, что в реестре; стояли английские литералы. */}
          <div>
            {m.sr_kind_label({
              kind: sr.kind === 'opening_stock' ? 'opening_stock' : 'physical_count',
            })}
          </div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.route_sr_col_status()}</div>
          <div>
            {/* F2: плашка рисовалась инлайновыми стилями с захардкоженными
                токенами — пятая поверхность одного статуса со своей геометрией
                и своим цветом. Теперь тот же бейдж, что везде. */}
            <DocStatusBadge status={(sr.status ?? 'draft') as DocStatusValue} size="sm" />
          </div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.route_sr_col_posting_date()}</div>
          <div>{formatDate(sr.posting_datetime ?? sr.created_at)}</div>
        </div>
      </div>
      {sr.notes && (
        <div className={shared.detailCard}>
          <div className={shared.detailRow}>
            <span className={shared.detailRowLabel}>{m.invoice_detail_notes()}</span>
            <strong>{sr.notes}</strong>
          </div>
        </div>
      )}
      <Table
        columns={lineColumns}
        data={sr.lines ?? []}
        rowKey={(r, i) => `${r.item_id}-${i}`}
        ariaLabel={m.route_sr_preview_lines_aria()}
      />
      <div className={shared.subtle} style={{ marginTop: 'var(--space-3)' }}>
        {m.detail_last_updated()}: {formatDate(sr.updated_at)}
      </div>
    </div>
  );
}
